import type { Match, Table, FlicButton, GameType, CheckIn } from '@shared/types';

// Allow runtime override of API base URL via a global set early in index.html
declare global {
  interface Window {
    __API_BASE_URL?: string;
  }
}

const API_BASE_URL =
  (typeof window !== 'undefined' && window.__API_BASE_URL) ||
  import.meta.env.VITE_API_URL ||
  '/api';

interface ResetConfirmPayload {
  confirmed: boolean;
}

interface CreateTablePayload {
  name: string;
  location?: string;
}

interface UpdateTablePayload {
  name?: string;
  location?: string;
}

interface AssignButtonPayload {
  serialNumber: string;
  buttonType: 'sideA' | 'sideB';
}

interface CheckInPayload {
  playerName: string;
  gameType: string;
  qrCodeType: 'player1' | 'player2';
}

interface CheckInResponse {
  checkInId: string;
  waitingForMatch: boolean;
  match?: {
    id: string;
    playerName: string;
    matchId?: string;
  };
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    let errorMessage = 'An error occurred';
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } catch {
      // If JSON parsing fails, use the default message
    }
    throw new ApiError(response.status, errorMessage);
  }

  return response.json();
}

export const api = {
  /**
   * Get match details by ID
   */
  getMatch: async (matchId: string): Promise<Match> => {
    return fetchApi<Match>(`/matches/${matchId}`);
  },

  /**
   * Confirm or cancel a reset request
   */
  confirmReset: async (matchId: string, confirmed: boolean): Promise<{ success: boolean; match: Match }> => {
    return fetchApi<{ success: boolean; match: Match }>(`/matches/${matchId}/reset-confirm`, {
      method: 'POST',
      body: JSON.stringify({ confirmed } as ResetConfirmPayload),
    });
  },

  // Table management
  /**
   * Get all tables
   */
  getTables: async (): Promise<{ tables: Table[] }> => {
    return fetchApi<{ tables: Table[] }>('/tables');
  },

  /**
   * Get table by ID
   */
  getTable: async (tableId: string): Promise<Table> => {
    return fetchApi<Table>(`/tables/${tableId}`);
  },

  /**
   * Create a new table
   */
  createTable: async (payload: CreateTablePayload): Promise<Table> => {
    const token = import.meta.env.VITE_BEARER_TOKEN;
    return fetchApi<Table>('/tables', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },

  /**
   * Update a table
   */
  updateTable: async (tableId: string, payload: UpdateTablePayload): Promise<Table> => {
    const token = import.meta.env.VITE_BEARER_TOKEN;
    return fetchApi<Table>(`/tables/${tableId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },

  /**
   * Delete a table
   */
  deleteTable: async (tableId: string): Promise<void> => {
    const token = import.meta.env.VITE_BEARER_TOKEN;
    return fetchApi<void>(`/tables/${tableId}`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },

  // Flic Button management
  /**
   * Get buttons for a table
   */
  getTableButtons: async (tableId: string): Promise<{ buttons: FlicButton[] }> => {
    return fetchApi<{ buttons: FlicButton[] }>(`/tables/${tableId}/buttons`);
  },

  /**
   * Assign button to table
   */
  assignButton: async (tableId: string, payload: AssignButtonPayload): Promise<FlicButton> => {
    const token = import.meta.env.VITE_BEARER_TOKEN;
    return fetchApi<FlicButton>(`/tables/${tableId}/buttons`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },

  /**
   * Remove button from table
   */
  removeButton: async (tableId: string, buttonId: string): Promise<void> => {
    const token = import.meta.env.VITE_BEARER_TOKEN;
    return fetchApi<void>(`/tables/${tableId}/buttons/${buttonId}`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },

  // Check-in and game types
  /**
   * Check in at a table
   */
  checkIn: async (tableId: string, payload: CheckInPayload): Promise<CheckInResponse> => {
    return fetchApi<CheckInResponse>(`/tables/${tableId}/checkin`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Get table status
   */
  getTableStatus: async (tableId: string): Promise<{ tableId: string; tableName: string; waitingPlayers: CheckIn[] }> => {
    return fetchApi<{ tableId: string; tableName: string; waitingPlayers: CheckIn[] }>(`/tables/${tableId}/status`);
  },

  /**
   * Get available game types
   */
  getGameTypes: async (): Promise<{ gameTypes: GameType[] }> => {
    return fetchApi<{ gameTypes: GameType[] }>('/game-types');
  },
};
